/**
 * useListCsvExport — eksport widoku listy do pliku Excela z komunikatami (E16).
 *
 * Cienka nakładka na `runListCsvExport`: dokłada toasty (gotowe / obcięte limitem / pusty widok /
 * błąd), stan „trwa eksport" (blokada podwójnego kliknięcia) i pobranie pliku w przeglądarce.
 * Strona dostarcza tylko to, co zna: jak pobrać wiersze widoku i jakie kolumny pokazuje.
 */

import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FormattingRule } from '@saas/shared';

import { useToast } from '@/contexts/ToastContext';

import type { ExportColumn } from './cell-text';
import { downloadFile, type ListExportResponse, runListCsvExport } from './run-export';

export interface UseListCsvExportOptions<TRow> {
  /**
   * Id kolumn pliku = kolumny widoczne w tabeli, w jej kolejności. Wołane RAZ, w chwili kliknięcia.
   * `null` = ekran jeszcze niegotowy (katalog pól, układ listy albo widoczność kolumn się
   * ładują) — eksport się wtedy nie zaczyna, a użytkownik dostaje komunikat „lista się ładuje".
   */
  getColumnIds: () => string[] | null;
  /** Pobiera wiersze widoku (te same filtry co lista) dla podanych kolumn; `ids` = tylko zaznaczone. */
  fetchRows: (ids: string[] | undefined, columnIds: string[]) => Promise<ListExportResponse<TRow>>;
  /** Kolumny pliku dla ustalonej listy id (z odpowiedzią w ręku, np. etykiety odwołań). */
  buildColumns: (response: ListExportResponse<TRow>, columnIds: string[]) => ExportColumn<TRow>[];
  /** Podstawa nazwy pliku (bez daty i rozszerzenia). */
  filename: string;
  /** Reguły formatowania warunkowego z układu listy (`layoutConfig.formattingRules`). */
  formattingRules?: readonly FormattingRule[];
  /** Tytuł listy (nazwa strony) — trafia do wiersza tytułu pliku z datą pobrania; brak → bez tytułu. */
  heading?: string;
}

export interface UseListCsvExportResult {
  /** „Eksport listy do CSV" — cały przefiltrowany widok. */
  exportList: () => Promise<void>;
  /** „Eksport zaznaczonych do CSV" — tylko wskazane rekordy. */
  exportSelected: (ids: string[]) => Promise<void>;
  /** Trwa eksport — wywołania w tym czasie są ignorowane. */
  isExporting: boolean;
}

export function useListCsvExport<TRow>(
  options: UseListCsvExportOptions<TRow>,
): UseListCsvExportResult {
  const { t } = useTranslation();
  const toast = useToast();
  const [isExporting, setIsExporting] = useState(false);
  // Ref, nie stan: drugie kliknięcie w tej samej klatce widzi aktualną blokadę.
  const runningRef = useRef(false);
  // Najświeższe opcje bez przebudowy callbacków (strona buduje je w każdym renderze).
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const run = useCallback(
    async (ids?: string[]) => {
      if (runningRef.current) return;
      // Lista kolumn ustalana RAZ — ta sama idzie do żądania i do pliku (nagłówek zgodny z danymi).
      const columnIds = optionsRef.current.getColumnIds();
      if (columnIds === null) {
        toast.show({ variant: 'info', title: t('tenant.listActions.toast.exportNotReady') });
        return;
      }
      runningRef.current = true;
      setIsExporting(true);
      try {
        await runListCsvExport<TRow>({
          columnIds,
          fetchRows: (selected, cols) => optionsRef.current.fetchRows(selected, cols),
          buildColumns: (response, cols) => optionsRef.current.buildColumns(response, cols),
          filename: optionsRef.current.filename,
          ids,
          formattingRules: optionsRef.current.formattingRules,
          heading: optionsRef.current.heading
            ? t('tenant.listActions.exportHeading', {
                title: optionsRef.current.heading,
                date: new Date().toLocaleString('pl-PL', {
                  dateStyle: 'short',
                  timeStyle: 'short',
                }),
              })
            : undefined,
          download: downloadFile,
          onDone: (count) =>
            toast.show({
              variant: 'success',
              title: t('tenant.listActions.toast.exportDone', { count }),
            }),
          onTruncated: (count, total) =>
            toast.show({
              variant: 'warning',
              title: t('tenant.listActions.toast.exportTruncated', { count, total }),
            }),
          onEmpty: () =>
            toast.show({ variant: 'info', title: t('tenant.listActions.toast.exportEmpty') }),
        });
      } catch {
        toast.show({ variant: 'error', title: t('tenant.listActions.toast.exportError') });
      } finally {
        runningRef.current = false;
        setIsExporting(false);
      }
    },
    [t, toast],
  );

  const exportList = useCallback(() => run(), [run]);
  const exportSelected = useCallback((ids: string[]) => run(ids), [run]);
  return { exportList, exportSelected, isExporting };
}
